import { cn } from "@/shared/lib/cn";

export interface ShopHeroMotesProps {
  /** `false` = la persona pidió movimiento reducido: se quedan quietos. */
  motionOk: boolean;
}

/**
 * Los tres elementos flotantes del fondo.
 *
 * Círculos desenfocados en lila, violeta y lavanda que suben y bajan muy
 * despacio. Son los mismos halos que el sistema usa en el resto de las
 * pantallas (`bg-ds-halo-lilac` en la portada anterior, `.itb-orb` en las
 * tarjetas), traídos aquí para que la portada nueva pertenezca al mismo
 * mundo que la vieja y no parezca un injerto.
 *
 * ── Se mueven con `animate-float-silk`, del sistema ──────────────────
 * Ya existe en `tailwind.config.cjs`: catorce segundos, `translateY` +
 * `rotate` + `scale`, todo `transform`. Se le cambia la duración y el
 * retardo por elemento para que los tres no respiren a la vez, que es lo
 * que delata una animación repetida.
 *
 * El retardo NEGATIVO no es un error: arranca la animación ya empezada,
 * así los tres círculos están en fases distintas desde el primer
 * fotograma en vez de salir alineados y separarse poco a poco.
 *
 * ── Las opacidades están topadas por la medición del contraste ───────
 * El peor caso se calculó con un elemento flotante al 14 %; ninguno de
 * estos pasa del 12 %, y además van desenfocados, que reparte el color en
 * vez de concentrarlo.
 */
const MOTES = [
  {
    position: "left-[6%] top-[16%] h-40 w-40 sm:h-52 sm:w-52",
    tint: "bg-ds-lilac/10",
    duration: "17s",
    delay: "0s",
  },
  {
    position: "right-[10%] top-[54%] h-56 w-56 sm:h-72 sm:w-72",
    tint: "bg-ds-violet-pop/[0.12]",
    duration: "21s",
    delay: "-6s",
  },
  {
    position: "left-[42%] top-[6%] h-24 w-24 sm:h-32 sm:w-32",
    tint: "bg-ds-lavender/[0.12]",
    duration: "13s",
    delay: "-9s",
  },
] as const;

export function ShopHeroMotes({ motionOk }: ShopHeroMotesProps) {
  return (
    <>
      {MOTES.map((mote) => (
        <div
          key={mote.position}
          className={cn(
            "absolute rounded-full blur-3xl",
            mote.position,
            mote.tint,
            motionOk && "animate-float-silk",
          )}
          style={
            motionOk
              ? { animationDuration: mote.duration, animationDelay: mote.delay }
              : undefined
          }
        />
      ))}
    </>
  );
}
